import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import {
  selectBusinessIntegrationPartners,
  selectProjects,
  selectSelectedProject,
} from 'store/projects/selectors/projectsSelectors';
import {
  selectUserActiveEmployerId,
  selectUserActiveEmployerInfo,
} from 'store/user/selectors.js/user.selectors';
import { closeReportSendDialog } from '../actions/reports.actions';
import {
  selectAdditionalUserSalaryRows,
  selectIsRecordBasedReductionsActive,
  selectIsReportProjectHourRowsEnabled,
  selectPaidCoffeeBreakMinutes,
  selectReportBasicData,
  selectReportPreviewWeeklyPeriodTableData,
  selectReportUserRowTotals,
  selectRowComments,
  selectSalaryRowsRecipientEmails,
  selectSeperateAdditionalUserSalaryRows,
} from '../selectors/reports.selectors';
import { postSalaryRowsInformation } from '../services/postSalaryRows.service';
import { returnMepcoWorkSalaryRows } from '../utils/returnMepcoWorkSalaryRows.util';
import { returnSentReportDataToSave } from '../utils/returnSentReportDataToSave';

export const sendSalaryData = createAsyncThunk(
  'reports/sendSalaryData',
  async (integrationPartnerOverrideValue, { getState, dispatch }) => {
    const reportBasicData = selectReportBasicData(getState());

    const activeEmployerId = selectUserActiveEmployerId(getState());

    const projects = selectProjects(getState());

    const selectedProject = selectSelectedProject(getState());
    const recipientEmailAddresses = selectSalaryRowsRecipientEmails(getState());

    const rowComments = selectRowComments(getState());

    const navigationActiveEmployer = projects[activeEmployerId];

    const additionalUserSalaryRows = selectAdditionalUserSalaryRows(getState());

    const seperateAdditionalUserSalaryRows =
      selectSeperateAdditionalUserSalaryRows(getState());

    const reportUserRowTotals = selectReportUserRowTotals(getState());

    const weeklyTableData = selectReportPreviewWeeklyPeriodTableData(
      getState()
    );

    const isRecordBasedReductions = selectIsRecordBasedReductionsActive(
      getState()
    );

    const businessIntegrationPartners = selectBusinessIntegrationPartners(
      getState()
    );

    const isProjectHourRowsEnabled = selectIsReportProjectHourRowsEnabled(
      getState()
    );

    const paidCoffeeBreakMinutes = selectPaidCoffeeBreakMinutes(getState());

    const activeEmployerInfo = selectUserActiveEmployerInfo(getState());

    let businessId;

    if (navigationActiveEmployer) {
      businessId = navigationActiveEmployer.business_id;
    } else {
      businessId = selectedProject.business_id;
    }

    let effectiveBusinessIntegrationPartners = [];

    if (integrationPartnerOverrideValue) {
      effectiveBusinessIntegrationPartners = [integrationPartnerOverrideValue];
    } else {
      const businessIntegrationPartnersOfBusiness =
        businessIntegrationPartners[businessId];

      effectiveBusinessIntegrationPartners =
        businessIntegrationPartnersOfBusiness;
    }

    let userSalaryRows = [];

    const isFennoaIntegration =
      effectiveBusinessIntegrationPartners?.includes('Fennoa');

    const isLemonsoftIntegration =
      effectiveBusinessIntegrationPartners?.includes('LEMONSOFT');

    const isProcountorIntegration =
      effectiveBusinessIntegrationPartners?.includes('PROCOUNTOR');

    Object.values(reportUserRowTotals).forEach((row) => {
      const {
        salaryType,
        name,
        unitCount,
        userId,
        personNumber,
        eachDayTotalHours,
        projectTotals,
      } = row;

      const rowComment = rowComments[userId];

      if (isFennoaIntegration && eachDayTotalHours) {
        eachDayTotalHours.forEach((dayTotals) => {
          const { day, hours } = dayTotals;
          userSalaryRows.push({
            userId,
            salaryType,
            unitCount: hours,
            date: dayjs(day).format('DD.MM.YYYY'),
            comment: rowComment,
            personName: name,
            personNumber,
          });
        });
      } else if (isLemonsoftIntegration) {
        if (salaryType === 'WORK_SALARY_MONTHLY') {
          userSalaryRows.push({
            userId,
            salaryType,
            unitCount: 1,
            date: eachDayTotalHours
              ? eachDayTotalHours[0]?.day
              : reportBasicData.endDate,
            comment: rowComment,
            personName: name,
            personNumber,
          });
        } else {
          if (isProjectHourRowsEnabled) {
            const effectiveEachDayTotalHours = eachDayTotalHours || [];

            effectiveEachDayTotalHours.forEach((dayTotals) => {
              const { day, eachProjectHours } = dayTotals;
              let remainingPaidCoffeeBreakMinutes = paidCoffeeBreakMinutes;

              Object.values(eachProjectHours).forEach((projectHourData) => {
                const { hours, talosoftaProjectId, reductions, projectName } =
                  projectHourData;

                let hoursWithReduction;

                if (isRecordBasedReductions) {
                  let totalReducedBreakHours = 0;

                  totalReducedBreakHours += reductions.BREAK;
                  totalReducedBreakHours += reductions.BREAK_MEAL;

                  if (reductions.BREAK_COFFEE > 0) {
                    // Convert coffee break from hours to minutes
                    const coffeeBreakMinutes = reductions.BREAK_COFFEE * 60;
                    // Calculate how much coffee break time to reduce from work hours
                    const coffeeBreakReduction =
                      Math.max(
                        0,
                        coffeeBreakMinutes - remainingPaidCoffeeBreakMinutes
                      ) / 60;
                    totalReducedBreakHours += coffeeBreakReduction;
                    remainingPaidCoffeeBreakMinutes = Math.max(
                      0,
                      remainingPaidCoffeeBreakMinutes - coffeeBreakMinutes
                    );
                  }

                  hoursWithReduction = hours - totalReducedBreakHours;
                } else {
                  hoursWithReduction =
                    reductions.AUTOMATIC_MEAL_BREAK_REDUCTION &&
                    reductions.AUTOMATIC_MEAL_BREAK_REDUCTION > 0
                      ? hours - reductions.AUTOMATIC_MEAL_BREAK_REDUCTION
                      : hours;
                }

                userSalaryRows.push({
                  talosoftaProjectId,
                  projectName,
                  userId,
                  salaryType,
                  unitCount: hoursWithReduction,
                  date: dayjs(day).format('DD.MM.YYYY'),
                  personName: name,
                  personNumber,
                });
              });
            });
          } else {
            const effectiveEachDayTotalHours = eachDayTotalHours || [];

            effectiveEachDayTotalHours.forEach((dayTotals) => {
              const { day, hours } = dayTotals;
              userSalaryRows.push({
                userId,
                salaryType,
                unitCount: hours,
                date: dayjs(day).format('DD.MM.YYYY'),
                comment: rowComment,
                personName: name,
                personNumber,
              });
            });
          }
        }
      } else if (isProcountorIntegration) {
        userSalaryRows.push({
          userId,
          salaryType,
          unitCount,
          projectName: '',
          assignmentName: '',
          earningPeriodStartDate: reportBasicData.fromDate,
          earningPeriodEndDate: reportBasicData.toDate,
          date: dayjs(reportBasicData.toDate).format('DD.MM.YYYY'),
          comment: rowComment,
          personName: name,
          personNumber,
        });
      } else {
        const salaryRows = returnMepcoWorkSalaryRows(
          row,
          reportBasicData,
          isProjectHourRowsEnabled,
          isRecordBasedReductions,
          paidCoffeeBreakMinutes
        );

        userSalaryRows.push(...salaryRows);
      }
    });

    if (isFennoaIntegration || isLemonsoftIntegration) {
      Object.values(seperateAdditionalUserSalaryRows).map((userRows) =>
        userRows.forEach((salaryRow) => {
          const {
            unitCount,
            salaryType,
            unitPrice,
            date,
            ISOStringDate,
            userId,
            talosoftaProjectId,
          } = salaryRow;

          if (salaryType === 'RIDE') {
            return;
          }
          const rowComment =
            rowComments[`${salaryRow.userId}-${salaryRow.salaryType}`];

          const manuallyAddedUser =
            salaryRow.userId?.includes('custom-added-user');

          const manuallyAddedMainSalaryRow = Object.values(
            Object.values(reportUserRowTotals)
          ).find((userRow) => userRow.userId === salaryRow.userId);

          userSalaryRows.push({
            userId: manuallyAddedUser ? undefined : userId,
            salaryType,
            unitCount,
            unitPrice,
            date: isLemonsoftIntegration ? ISOStringDate : date,
            comment: rowComment,
            personName: manuallyAddedUser
              ? manuallyAddedMainSalaryRow?.name
              : salaryRow.userName,
            personNumber: manuallyAddedMainSalaryRow?.personNumber,
            talosoftaProjectId: isProjectHourRowsEnabled
              ? talosoftaProjectId
              : '',
          });
        })
      );
    } else if (isProcountorIntegration) {
      Object.values(additionalUserSalaryRows).map((userRows) =>
        userRows.forEach((salaryRow) => {
          if (salaryRow.salaryType === 'RIDE') {
            return;
          }
          const rowComment =
            rowComments[`${salaryRow.userId}-${salaryRow.salaryType}`];

          const manuallyAddedUser =
            salaryRow.userId.includes('custom-added-user');

          const manuallyAddedMainSalaryRow = Object.values(
            Object.values(reportUserRowTotals)
          ).find((userRow) => userRow.userId === salaryRow.userId);

          userSalaryRows.push({
            userId: manuallyAddedUser ? undefined : salaryRow.userId,
            salaryType: salaryRow.salaryType,
            unitCount: salaryRow.unitCount,
            unitPrice: salaryRow.unitPrice,
            earningPeriodStartDate: reportBasicData.fromDate,
            earningPeriodEndDate: reportBasicData.toDate,
            comment: rowComment,
            personName: manuallyAddedUser
              ? manuallyAddedMainSalaryRow?.name
              : salaryRow.userName,
            personNumber: manuallyAddedMainSalaryRow?.personNumber,
          });
        })
      );
    } else {
      const absenceOrHolidayRows = [];

      const otherAdditionalSalaryRows = [];

      Object.values(seperateAdditionalUserSalaryRows).forEach((userRows) =>
        userRows.forEach((salaryRow) => {
          const {
            unitCount,
            salaryType,
            unitPrice,
            date,
            userId,
            talosoftaProjectId,
            userName,
          } = salaryRow;

          const rowComment =
            rowComments[`${salaryRow.userId}-${salaryRow.salaryType}`];

          const manuallyAddedUser =
            salaryRow.userId?.includes('custom-added-user');

          const manuallyAddedMainSalaryRow = Object.values(
            Object.values(reportUserRowTotals)
          ).find((userRow) => userRow.userId === salaryRow.userId);

          if (returnIsAbsenceOrHolidayRow(salaryType)) {
            absenceOrHolidayRows.push({
              userId: manuallyAddedUser ? undefined : userId,
              salaryType,
              unitCount,
              unitPrice,
              date,
              comment: rowComment,
              personName: manuallyAddedUser
                ? manuallyAddedMainSalaryRow?.name
                : salaryRow.userName,
              personNumber: manuallyAddedMainSalaryRow?.personNumber,
            });
          } else {
            if (salaryType !== 'RIDE' && isProjectHourRowsEnabled) {
              otherAdditionalSalaryRows.push({
                userId: manuallyAddedUser ? undefined : userId,
                salaryType,
                unitCount,
                unitPrice,
                date,
                comment: rowComment,
                personName: manuallyAddedUser
                  ? manuallyAddedMainSalaryRow?.name
                  : userName,
                personNumber: manuallyAddedMainSalaryRow?.personNumber,
                talosoftaProjectId,
              });
            }
          }
        })
      );

      if (!isProjectHourRowsEnabled) {
        Object.values(additionalUserSalaryRows).map((userRows) =>
          userRows.forEach((salaryRow) => {
            const rowComment =
              rowComments[`${salaryRow.userId}-${salaryRow.salaryType}`];

            const manuallyAddedUser =
              salaryRow.userId.includes('custom-added-user');

            const manuallyAddedMainSalaryRow = Object.values(
              Object.values(reportUserRowTotals)
            ).find((userRow) => userRow.userId === salaryRow.userId);

            if (
              !returnIsAbsenceOrHolidayRow(salaryRow.salaryType) &&
              salaryRow.salaryType !== 'RIDE'
            ) {
              otherAdditionalSalaryRows.push({
                userId: manuallyAddedUser ? undefined : salaryRow.userId,
                salaryType: salaryRow.salaryType,
                unitCount: salaryRow.unitCount,
                unitPrice: salaryRow.unitPrice,
                date: dayjs(reportBasicData.toDate).format('DD.MM.YYYY'),
                earningPeriodStartDate: reportBasicData.fromDate,
                earningPeriodEndDate: reportBasicData.toDate,
                comment: rowComment,
                personName: manuallyAddedUser
                  ? manuallyAddedMainSalaryRow?.name
                  : salaryRow.userName,
                personNumber: manuallyAddedMainSalaryRow?.personNumber,
              });
            }
          })
        );
      }

      userSalaryRows.push(
        ...[...absenceOrHolidayRows, ...otherAdditionalSalaryRows]
      );
    }

    const reportDataToSave = returnSentReportDataToSave({
      selectedProjectId: selectedProject.id,
      weeklyTableData,
      userAdditionalSalaryRows: additionalUserSalaryRows,
      reportBasicData,
      state: 'SENT',
      rowComments,
      reportUserRowTotals,
      isRecordBasedReductions,
    });

    const response = await postSalaryRowsInformation(
      {
        salaryRows: userSalaryRows,
        recipientEmailAddresses,
        reportDataToSave,
        integrationPartnerOverrideValue,
      },
      businessId,
      activeEmployerInfo
    );

    dispatch(closeReportSendDialog());

    return response;
  }
);

const returnIsAbsenceOrHolidayRow = (salaryType) => {
  const absenceTypeAllowances = Object.values(DEFAULT_ADDITIONAL_SALARY_TYPES)
    .filter((salaryType) => salaryType.baseType === 'ABSENCE')
    .map((salaryType) => salaryType.salaryType);

  const isAbsenceOrHolidayRow = absenceTypeAllowances.includes(salaryType);

  return isAbsenceOrHolidayRow;
};
